import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { my_routes } from "../../router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { updateUser } from "../../../api/users";
import { modifyPassword } from "../../../api/users";
import Swal from "sweetalert2";
import Avvvatars from "avvvatars-react";

// pour restreaindre l'accès à ce composant
import requireAuthentification from "../../../core/common/autorisations/requireAuthentification";
import { AxiosError } from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { loadUserDataThunk } from "../../../core/data/redux/authSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import { deleteAvatar, getAvatarFileById } from "../../../api/avatar";
import { Popconfirm } from "antd";
import UploadPhotoModal from "../../avatar/UploadPhotoModal";

type PasswordField =
  | "oldPassword"
  | "newPassword"
  | "confirmPassword"
  | "currentPassword";

// 1.Définition des  schémas zod
const editPersonalInformationShema = z.object({
  firstname: z
    .string({ message: "vous devez entrer un prénom valide" })
    .nonempty({ message: "ce champ est obligatoire " }),
  lastname: z
    .string({ message: "vous devez entrer un nom valide" })
    .nonempty({ message: "ce champ est obligatoire " }),
  // nomUtilisateur: z
  //   .string({ message: "vous devez entrer un nom utilisateur valide" })
  //   .nonempty({ message: "ce champ est obligatoire " }),
  email: z
    .string({ message: "vous devez entrer un email valide" })
    .email({ message: "ce champ doit être un email valide" }),
  number: z
    .string()
    .regex(
      /^(\+223)?[456789]\d{7}$/,
      "Numéro de téléphone invalide. Ex: +22370123456 ou 70123456",
    )
    .nonempty({ message: "ce champ est obligatoire " }),
  adresse: z
    .string({ message: "vous devez entrer une adresse valide" })
    .nonempty({ message: "ce champ est obligatoire " }),
  // bio: z.string({ message: "vous devez entrer une bio valide" }).optional(),
});

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    newPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    confirmPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: " Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "Le nouveau mot de passe doit être différent de l'ancien",
    path: ["newPassword"],
  });

//2.Inference de type des données des formulaires
type editPersonalInformationFormData = z.infer<
  typeof editPersonalInformationShema
>;
// type editAddressInformationFormData = z.infer<
//   typeof editAddressInformationSchema
// >;
type changePasswordFormData = z.infer<typeof changePasswordSchema>;

const Profile = () => {
  const route = my_routes;
  const dispatch = useDispatch<AppDispatch>();

  // id de l'utilisateur connecté
  const id = useSelector((state: any) => state?.auth?.user?.id);

  const [refresh, setRefresh] = useState(0);
  const [avatar, setAvatar] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
    currentPassword: false,
  });
  const [user, setUser] = useState({
    firstname: "",
    lastname: "",
    email: "",
    number: "",
    adresse: "",
  });

  // vérifier si un objet est vide
  function isEmpty(obj: any) {
    return Object.keys(obj).length === 0;
  }

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // utiliser react-hook-form pour chaque formulaire

  // 1. Formulaire informations personnelles
  const {
    register: registerPersonal,
    handleSubmit: handleSubmitPersonal,
    setError: setErrorPersonal,
    formState: { errors: errorsPersonal },
    reset: resetPersonal,
    clearErrors: clearErrorsPersonal,
  } = useForm<editPersonalInformationFormData>({
    resolver: zodResolver(editPersonalInformationShema),
  });

  const userReceived = useSelector((state: any) => state.auth.user);

  useEffect(() => {
    // récupérer les données de l'utilisateur depuis le store redux
    setUser(userReceived);
    resetPersonal(userReceived);
  }, [dispatch, resetPersonal, refresh]);

  // 3. Formulaire changement de mot de passe
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword },
    reset: resetPassword,
    clearErrors: clearErrorsPassword,
  } = useForm<changePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  // Fonctions de soumissions des formulaires d'édition

  const onSubmitPersonal = async (data: editPersonalInformationFormData) => {
    try {
      // console.log("Data envoyé avec succès: ", data);
      document.getElementById("closeBtn-personalModal")?.click();

      if (!id) {
        setErrorPersonal("root", {
          message:
            "Aucun id trouver pour effectuer cette opération, assurez-vous d'etre connecté",
        });
        return;
      }

      await updateUser(data, id);

      await dispatch(loadUserDataThunk()).unwrap();
      setRefresh((prev) => prev + 1);
      toast.success("Information mis à jour avec sucès");
      // setLocalStorage("userData", { ...user, data });
    } catch (error: any) {
      // console.log("error: modification Personal: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const onSubmitPassword = async (data: changePasswordFormData) => {
    try {
      // console.log("Data envoyé avec succès: ", data);
      const { currentPassword, newPassword } = data;
      const response = await modifyPassword({
        oldPassword: currentPassword,
        newPassword,
      });
      if (!response) {
        Swal.fire({
          title: "Erreur",
          text: "La modification du mot de passe a échoué.",
          icon: "error",
        });
        throw new AxiosError("La modification du mot de passe a échoué.");
      }
      Swal.fire({
        title: "Succès",
        text: "Votre mot de passe a été changé avec succès.",
        icon: "success",
      });
      // masquer le modal
      document.getElementById("closeBtn-passwordModal")?.click();
      resetPassword();
      // clearErrorsPassword();
    } catch (error: any) {
      // console.log(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      // setErrorPassword("root", {
      //   message:
      //     error instanceof Error ? error.message : "une erreur c'est produite",
      // });
    }
  };

  const handleAddPhoto = () => {
    setShowUploadModal(true);
  };

  const handleDeletePhoto = async () => {
    try {
      if (!avatar || !id) return;
      await deleteAvatar(avatar?.id);
      loadAvatar();
      toast.success("Photo supprimee avec succes");
    } catch (error: any) {
      // console.log("Erreur lors de la suppression: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Charger la photo de profil (avatar)
  const loadAvatar = async () => {
    try {
      // Récupérer URL temporaire API
      const resp = await getAvatarFileById("UserProfil", id);
      const tempUrl = resp.data;
      setAvatar(tempUrl);
    } catch (error: any) {
      // console.log("Erreur lors de la recuperation de l'avatar: ", error);
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    loadAvatar();
  }, []);

  return (
    <div>
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between border-bottom pb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Profile</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={route.adminDashboard}>Accueil</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Paramètres</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Mon Profil
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="pe-1 mb-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-top">Rafraîchir</Tooltip>}
                  >
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white btn-icon me-1"
                    >
                      <i className="ti ti-refresh" />
                    </Link>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
            {errorsPersonal.root && (
              <div className="alert alert-danger">
                {errorsPersonal.root.message}
              </div>
            )}
            {errorsPassword.root && (
              <div className="alert alert-danger">
                {errorsPassword.root.message}
              </div>
            )}
            <div className="d-md-flex d-block mt-3">
              <div className="settings-right-sidebar me-md-3 border-0">
                <div className="card">
                  <div className="card-header">
                    <h5>Information Personnel</h5>
                  </div>
                  <div className="card-body ">
                    <div className="settings-profile-upload">
                      <div className="avatar-wrapper">
                        {avatar?.temporaryUrl ? (
                          <img
                            src={avatar?.temporaryUrl}
                            alt={`${user?.lastname} ${user?.firstname}`}
                            className="avatar-image"
                          />
                        ) : (
                          <div className="">
                            <Avvvatars
                              value={`${user.firstname?.charAt(0) || ""}${
                                user.lastname?.charAt(0) || ""
                              }`}
                              style="character"
                              size={60}
                            />
                          </div>
                        )}
                      </div>
                      <div className="title-upload">
                        <h5>Editer Ta Photo</h5>
                        <Popconfirm
                          title="Êtes-vous sûr de vouloir supprimer cette photo ?"
                          onConfirm={() => handleDeletePhoto()}
                          disabled={!avatar?.temporaryUrl}
                          okText="Oui"
                          cancelText="Non"
                        >
                          <Link to="#" className="me-2">
                            Supprimer{" "}
                          </Link>
                        </Popconfirm>
                        <Link
                          to="#"
                          className="text-primary"
                          onClick={() => handleAddPhoto()}
                        >
                          Mettre à jour
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-fill ps-0 border-0">
                <form>
                  <div className="d-md-flex">
                    <div className="flex-fill">
                      <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5>Information Personnel</h5>
                          <Link
                            to="#"
                            className="btn btn-primary btn-sm"
                            data-bs-toggle="modal"
                            data-bs-target="#edit_personal_information"
                            onClick={() => {
                              if (!isEmpty(errorsPersonal))
                                clearErrorsPersonal();
                            }}
                          >
                            <i className="ti ti-edit me-2" />
                            Editer
                          </Link>
                        </div>
                        <div className="card-body pb-0">
                          <div className="d-block d-xl-flex">
                            <div className="mb-3 flex-fill me-xl-3 me-0">
                              <label className="form-label">Prénom</label>
                              <input
                                type="text"
                                defaultValue={user.firstname}
                                className="form-control"
                                placeholder="Entrez le prénom"
                                readOnly
                                disabled={true}
                              />
                            </div>
                            <div className="mb-3 flex-fill">
                              <label className="form-label">Nom</label>
                              <input
                                type="text"
                                defaultValue={user.lastname}
                                className="form-control"
                                placeholder="Entrez le nom"
                                readOnly
                                disabled={true}
                              />
                            </div>
                          </div>
                          <div className="d-block d-xl-flex">
                            <div className="mb-3 flex-fill me-xl-3 me-0">
                              <label className="form-label">Email</label>
                              <input
                                type="text"
                                defaultValue={user.email}
                                className="form-control"
                                placeholder="Entrez l'email"
                                readOnly
                                disabled={true}
                              />
                            </div>
                            <div className="mb-3 flex-fill">
                              <label className="form-label">
                                Nom d'utilisateur
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                defaultValue={
                                  user.firstname && user.lastname
                                    ? `${user.firstname}  ${user.lastname}`
                                    : ""
                                }
                                placeholder="Entrez le nom d'utilisateur"
                                readOnly
                                disabled={true}
                              />
                            </div>
                          </div>

                          <div className="d-block d-xl-flex">
                            <div className="mb-3 flex-fill me-xl-3 me-0">
                              <label className="form-label">
                                Numéro de téléphone
                              </label>
                              <input
                                type="text"
                                defaultValue={user.number}
                                className="form-control"
                                placeholder="Entrez le numéro de téléphone"
                                readOnly
                                disabled={true}
                              />
                            </div>
                            <div className="mb-3 flex-fill">
                              <label className="form-label">Adresse</label>
                              <input
                                type="text"
                                defaultValue={user.adresse}
                                className="form-control"
                                placeholder="Entrer Adresse"
                                readOnly
                                disabled={true}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5>Mot de passe</h5>
                          <Link
                            to="#"
                            className="btn btn-primary btn-sm"
                            data-bs-toggle="modal"
                            data-bs-target="#change_password"
                            onClick={() => {
                              if (!isEmpty(errorsPassword))
                                clearErrorsPassword();
                            }}
                          >
                            Changer
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}
        {/* Edit Profile */}
        <div className="modal fade" id="edit_personal_information">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  Editer les informations personnelles
                </h4>
                <button
                  id="closeBtn-personalModal"
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form
                className="needs-validation"
                onSubmit={handleSubmitPersonal(onSubmitPersonal)}
              >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Prénom</label>
                        <input
                          id="edit_prenom_personal"
                          {...registerPersonal("firstname")}
                          type="text"
                          className={
                            errorsPersonal.firstname
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Entrez le prénom"
                        />
                      </div>
                      {errorsPersonal.firstname && (
                        <div className="text-danger">
                          {errorsPersonal.firstname.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">Nom de famille</label>
                        <input
                          id="edit_nom_personal"
                          {...registerPersonal("lastname")}
                          type="text"
                          className={
                            errorsPersonal.lastname
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Entrez le nom de famille"
                        />
                      </div>
                      {errorsPersonal.lastname && (
                        <div className="text-danger">
                          {errorsPersonal.lastname.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">Email</label>
                        <input
                          id="edit_email_personnal"
                          {...registerPersonal("email")}
                          type="email"
                          className={
                            errorsPersonal.email
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Entrez l'email"
                        />
                      </div>
                      {errorsPersonal.email && (
                        <div className="text-danger">
                          {errorsPersonal.email.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">
                          Numéro de téléphone
                        </label>
                        <input
                          id="edit_phone_number_personal"
                          {...registerPersonal("number")}
                          type="text"
                          className={
                            errorsPersonal.number
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Entrez le numéro de téléphone"
                        />
                      </div>
                      {errorsPersonal.number && (
                        <div className="text-danger">
                          {errorsPersonal.number.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">Adresse</label>
                        <input
                          id="edit_adresse_address"
                          {...registerPersonal("adresse")}
                          type="text"
                          className={
                            errorsPersonal.adresse
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Entrez l'adresse"
                        />
                      </div>
                      {errorsPersonal.adresse && (
                        <div className="text-danger">
                          {errorsPersonal.adresse.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={() => resetPassword()}
                  >
                    Annuler
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    // data-bs-dismiss="modal"
                  >
                    Enregistrer les modifications
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Edit Profile */}

        {/* Change Password */}
        <div className="modal fade" id="change_password">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Changer le mot de passe</h4>
                <button
                  id="closeBtn-passwordModal"
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form
                className="needs-validation"
                onSubmit={handleSubmitPassword(onSubmitPassword)}
              >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Mot de passe actuel
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_currentPassword_password"
                            {...registerPassword("currentPassword")}
                            type={
                              passwordVisibility.oldPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.oldPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("oldPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.currentPassword && (
                        <div className="text-danger">
                          {errorsPassword.currentPassword.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">
                          Nouveau mot de passe
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_newPassword_password"
                            {...registerPassword("newPassword")}
                            type={
                              passwordVisibility.newPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.newPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("newPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.newPassword && (
                        <div className="text-danger">
                          {errorsPassword.newPassword.message}
                        </div>
                      )}
                      <div className="mb-0">
                        <label className="form-label">
                          Confirmer le mot de passe
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_confirmPassword_password"
                            {...registerPassword("confirmPassword")}
                            type={
                              passwordVisibility.confirmPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.confirmPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("confirmPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.confirmPassword && (
                        <div className="text-danger">
                          {errorsPassword.confirmPassword.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Annuler
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    // data-bs-dismiss="modal"
                  >
                    Enregistrer les modifications
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Change Password */}
        <UploadPhotoModal
          profilId={id}
          profilType="UserProfil"
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            loadAvatar();
          }}
        />
      </>
    </div>
  );
};

export default requireAuthentification(Profile);
